import { HonchInstance, HonchConfig } from '../config';
import { Logger } from '../utils/logger';
import { getElementSelector, getElementText, shouldIgnoreElement } from './utils';

export class ClickTracker {
    private honch: HonchInstance;
    private config: HonchConfig;
    private logger: Logger;

    constructor(honch: HonchInstance, config: HonchConfig, logger: Logger) {
        this.honch = honch;
        this.config = config;
        this.logger = logger;
    }

    start(): void {
        if (typeof document === 'undefined') return;

        document.addEventListener('click', this.handleClick, { capture: true });
        this.logger.debug('Click tracker started');
    }

    stop(): void {
        if (typeof document === 'undefined') return;

        document.removeEventListener('click', this.handleClick, { capture: true });
        this.logger.debug('Click tracker stopped');
    }

    private handleClick = (event: MouseEvent): void => {
        const target = event.target as HTMLElement;
        if (!target) return;

        // Find the most relevant clickable element
        const element = this.findClickableElement(target);
        if (!element) return;

        // Check if element should be ignored
        if (shouldIgnoreElement(element, this.config.exclude_elements)) {
            return;
        }

        const properties = this.getElementProperties(element);

        this.honch.capture('$click', properties);

        this.logger.debug('Click captured', properties);
    };

    private findClickableElement(target: HTMLElement): HTMLElement | null {
        let element: HTMLElement | null = target;
        const maxDepth = 5;
        let depth = 0;

        while (element && depth < maxDepth) {
            const tagName = element.tagName.toLowerCase();

            // Common clickable elements
            if (
                tagName === 'a' ||
                tagName === 'button' ||
                tagName === 'input' ||
                tagName === 'select' ||
                element.hasAttribute('role') ||
                element.hasAttribute('onclick')
            ) {
                return element;
            }

            element = element.parentElement;
            depth++;
        }

        return target;
    }

    private getElementProperties(element: HTMLElement): Record<string, unknown> {
        const tagName = element.tagName.toLowerCase();

        return {
            $element_tag: tagName,
            $element_text: getElementText(element),
            $element_selector: getElementSelector(element),
            $element_classes: element.className || undefined,
            $element_id: element.id || undefined,
            $element_href: (element as HTMLAnchorElement).href || undefined,
            $element_name: (element as HTMLInputElement).name || undefined,
            $element_type: (element as HTMLInputElement).type || undefined,
        };
    }
}
